import React, { Component, ReactNode, ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RouteFallback = ({ error, resetError }: { error: Error; resetError: () => void }) => {
    const navigate = useNavigate();

    return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center h-full w-full bg-white rounded-xl shadow-sm border border-app-border m-6">
            <div className="w-16 h-16 bg-status-warn-bg rounded-full flex items-center justify-center mb-6">
                <AlertTriangle className="w-8 h-8 text-status-warn" />
            </div>

            <h2 className="text-xl font-bold text-text-ink mb-2">Failed to load this page</h2>
            <p className="text-text-muted mb-8 max-w-md">
                An unexpected error occurred while rendering this section.
                Our team has been notified.
            </p>

            {/* Dev-only error preview */}
            {import.meta.env.DEV && (
                <div className="mb-8 p-4 bg-status-warn-bg text-status-warn rounded text-left overflow-auto max-w-xl w-full text-xs font-mono">
                    <p className="font-bold mb-1">{error.name}: {error.message}</p>
                </div>
            )}

            <div className="flex items-center gap-4">
                <button
                    onClick={() => resetError()}
                    className="flex items-center gap-2 px-5 py-2.5 bg-app-bg text-text-ink rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                </button>
                <button
                    onClick={() => {
                        resetError();
                        navigate('/dashboard');
                    }}
                    className="flex items-center gap-2 px-5 py-2.5 bg-action text-white rounded-lg font-medium hover:bg-action-dark transition-colors"
                >
                    <Home className="w-4 h-4" />
                    Go to Dashboard
                </button>
            </div>
        </div>
    );
};

interface RouteErrorBoundaryProps {
    children: ReactNode;
    resetKey?: string;
    key?: string;
}

export const RouteErrorBoundary = ({ children }: RouteErrorBoundaryProps) => {
    return (
        <Sentry.ErrorBoundary fallback={RouteFallback}>
            {children}
        </Sentry.ErrorBoundary>
    );
};
